<script setup lang="ts">
/**
 * QuickFilterPanel
 *
 * Collapsible inline filter panel that renders entity-specific
 * quick controls (search, dropdowns, number inputs, date pickers, toggles).
 *
 * Reads current values from a FilterGroup for bidirectional sync
 * with the command bar's chip system.
 */

import { ref, computed, watch, onMounted } from 'vue'
import type { QuickFilterConfig } from '../composables/quickFilterConfigs'
import type { FilterGroup, FilterCondition, FilterOperator, SavedViewEntityType, SchemaField } from '../api/types'
import { isFilterCondition } from '../api/types'
import { savedViewService } from '../api/service'
import MultiSelectDropdown from '@/components/MultiSelectDropdown.vue'
import FilterSelect from './FilterSelect.vue'
import type { MultiSelectOption } from '@/components/MultiSelectDropdown.vue'

interface Props {
    configs: QuickFilterConfig[]
    currentFilters?: FilterGroup
    entityType: SavedViewEntityType
    /** Schema fields — used to validate config field names */
    fields?: SchemaField[]
}

const props = defineProps<Props>()

const emit = defineEmits<{
    'update-filter': [field: string, op: FilterOperator, value: unknown]
    'remove-filter': [field: string, op: FilterOperator]
    'open-advanced': []
    'clear-all': []
}>()

/* ── Local input state (mirrors currentFilters) ───────────────── */

/** Map of "field:op" → current value for each quick filter control */
const localValues = ref<Record<string, unknown>>({})

/** Loaded options for multi-select fields (fetched from API) */
const fieldOptions = ref<Record<string, MultiSelectOption[]>>({})

/** Active filter count for "Clear all" */
const activeCount = computed(() => {
    return Object.values(localValues.value).filter(v => {
        if (v === undefined || v === null || v === '') return false
        if (Array.isArray(v)) return v.length > 0
        return true
    }).length
})

/* ── Config validation against schema ─────────────────────────── */

/**
 * Only render configs whose field exists in the loaded schema.
 * The 'search' pseudo-field is always allowed.
 */
const validatedConfigs = computed(() => {
    if (!props.fields || props.fields.length === 0) return props.configs
    const schemaNames = new Set(props.fields.map(f => f.name))
    return props.configs.filter(c => c.field === 'search' || schemaNames.has(c.field))
})

/* ── Config grouping ──────────────────────────────────────────── */

const row1Configs = computed(() => validatedConfigs.value.filter(c => (c.row ?? 1) === 1))
const row2Configs = computed(() => validatedConfigs.value.filter(c => (c.row ?? 1) === 2))
const hasRow2 = computed(() => row2Configs.value.length > 0)

/* ── Key helper ───────────────────────────────────────────────── */

function configKey(config: QuickFilterConfig): string {
    return `${config.field}:${config.operator}`
}

/* ── Sync: currentFilters → local values ──────────────────────── */

function syncFromFilters() {
    const values: Record<string, unknown> = {}
    if (props.currentFilters?.conditions) {
        for (const cond of props.currentFilters.conditions) {
            if (!isFilterCondition(cond)) continue
            const fc = cond as FilterCondition
            // Match against our configs
            for (const cfg of props.configs) {
                if (fc.field === cfg.field && fc.op === cfg.operator) {
                    values[configKey(cfg)] = fc.value
                }
            }
        }
    }
    // Set missing config keys to empty
    for (const cfg of props.configs) {
        const key = configKey(cfg)
        if (!(key in values)) {
            values[key] = cfg.control === 'multi-select' ? [] : ''
        }
    }
    localValues.value = values
}

watch(() => props.currentFilters, syncFromFilters, { deep: true })
onMounted(() => {
    syncFromFilters()
    loadMultiSelectOptions()
})

/* ── Load options for multi-select fields ─────────────────────── */

async function loadMultiSelectOptions() {
    for (const cfg of props.configs) {
        // Handle multi-select options (existing behavior)
        if (cfg.control === 'multi-select') {
            if (cfg.options) {
                // Static options provided in config
                fieldOptions.value[cfg.field] = cfg.options.map(o => ({
                    value: o.value,
                    label: o.label,
                }))
            } else {
                // Load from API
                try {
                    const values = await savedViewService.getFieldValues(props.entityType, cfg.field)
                    if (Array.isArray(values) && values.length > 0) {
                        if (typeof values[0] === 'string') {
                            fieldOptions.value[cfg.field] = (values as string[]).map(v => ({
                                value: v,
                                label: v,
                            }))
                        } else {
                            fieldOptions.value[cfg.field] = (values as { id: string; label: string }[]).map(v => ({
                                value: v.id,
                                label: v.label,
                            }))
                        }
                    }
                } catch {
                    fieldOptions.value[cfg.field] = []
                }
            }
        }

        // Handle select controls with asyncOptions (dynamic options)
        if (cfg.control === 'select' && cfg.asyncOptions && !cfg.options) {
            try {
                const loaded = await cfg.asyncOptions()
                fieldOptions.value[cfg.field] = loaded.map(o => ({
                    value: o.value,
                    label: o.label,
                }))
            } catch {
                fieldOptions.value[cfg.field] = []
            }
        }
    }
}

/* ── Change handlers ──────────────────────────────────────────── */

let searchTimeout: ReturnType<typeof setTimeout> | null = null
let numberTimeout: ReturnType<typeof setTimeout> | null = null

function onSearchInput(config: QuickFilterConfig, event: Event) {
    const val = (event.target as HTMLInputElement).value
    localValues.value[configKey(config)] = val
    if (searchTimeout) clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
        if (val) {
            emit('update-filter', config.field, config.operator, val)
        } else {
            emit('remove-filter', config.field, config.operator)
        }
    }, 300)
}

function onSelectChange(config: QuickFilterConfig, eventOrValue: Event | string) {
    const val = typeof eventOrValue === 'string'
        ? eventOrValue
        : (eventOrValue.target as HTMLSelectElement).value
    localValues.value[configKey(config)] = val
    if (val) {
        emit('update-filter', config.field, config.operator, val)
    } else {
        emit('remove-filter', config.field, config.operator)
    }
}

function onMultiSelectChange(config: QuickFilterConfig, values: string[]) {
    localValues.value[configKey(config)] = values
    if (values.length > 0) {
        emit('update-filter', config.field, config.operator, values)
    } else {
        emit('remove-filter', config.field, config.operator)
    }
}

function onNumberInput(config: QuickFilterConfig, event: Event) {
    const val = (event.target as HTMLInputElement).value
    localValues.value[configKey(config)] = val
    if (numberTimeout) clearTimeout(numberTimeout)
    numberTimeout = setTimeout(() => {
        if (val !== '' && val !== undefined) {
            emit('update-filter', config.field, config.operator, Number(val))
        } else {
            emit('remove-filter', config.field, config.operator)
        }
    }, 300)
}

function onDateChange(config: QuickFilterConfig, event: Event) {
    const val = (event.target as HTMLInputElement).value
    localValues.value[configKey(config)] = val
    if (val) {
        emit('update-filter', config.field, config.operator, val)
    } else {
        emit('remove-filter', config.field, config.operator)
    }
}

function onBooleanToggle(config: QuickFilterConfig, value: boolean | null) {
    const key = configKey(config)
    const current = localValues.value[key]
    if (current === value) {
        // Toggle off — remove filter
        localValues.value[key] = ''
        emit('remove-filter', config.field, config.operator)
    } else {
        localValues.value[key] = value
        emit('update-filter', config.field, config.operator, value)
    }
}

function onClearAll() {
    emit('clear-all')
}

/* ── Width class helper ───────────────────────────────────────── */

function widthClass(config: QuickFilterConfig): string {
    if (config.width === 'narrow') return 'qf-group--narrow'
    if (config.width === 'wide') return 'qf-group--wide'
    return ''
}
</script>

<template>
    <div class="quick-filter-panel">
        <!-- Row 1 -->
        <div class="qf-row">
            <template v-for="cfg in row1Configs" :key="configKey(cfg)">
                <!-- Search -->
                <div v-if="cfg.control === 'search'" :class="['qf-group', widthClass(cfg)]">
                    <div class="qf-search-wrapper">
                        <svg class="qf-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.35-4.35" />
                        </svg>
                        <input
                            type="text"
                            class="qf-search-input"
                            :placeholder="cfg.placeholder || 'Search...'"
                            :value="String(localValues[configKey(cfg)] ?? '')"
                            @input="onSearchInput(cfg, $event)"
                        />
                    </div>
                </div>

                <!-- Select -->
                <div v-else-if="cfg.control === 'select'" :class="['qf-group', widthClass(cfg)]">
                    <label class="qf-label">{{ cfg.label }}</label>
                    <FilterSelect
                        :model-value="String(localValues[configKey(cfg)] ?? '')"
                        :options="fieldOptions[cfg.field] ?? cfg.options ?? []"
                        :placeholder="cfg.placeholder || 'All'"
                        @update:model-value="(val: string) => onSelectChange(cfg, val)"
                    />
                </div>

                <!-- Multi-Select -->
                <div v-else-if="cfg.control === 'multi-select'" :class="['qf-group', widthClass(cfg)]">
                    <MultiSelectDropdown
                        :model-value="Array.isArray(localValues[configKey(cfg)]) ? (localValues[configKey(cfg)] as string[]) : []"
                        :options="fieldOptions[cfg.field] ?? []"
                        :label="cfg.label"
                        :placeholder="cfg.placeholder || 'All'"
                        @update:model-value="(v: string[]) => onMultiSelectChange(cfg, v)"
                    />
                </div>
            </template>
        </div>

        <!-- Row 2 -->
        <div v-if="hasRow2" class="qf-row qf-row--secondary">
            <template v-for="cfg in row2Configs" :key="configKey(cfg)">
                <!-- Number -->
                <div v-if="cfg.control === 'number'" :class="['qf-group', widthClass(cfg)]">
                    <label class="qf-label">{{ cfg.label }}</label>
                    <input
                        type="number"
                        class="qf-input qf-input--number"
                        :placeholder="cfg.placeholder || '—'"
                        :step="cfg.step"
                        :min="cfg.min"
                        :value="localValues[configKey(cfg)] ?? ''"
                        @input="onNumberInput(cfg, $event)"
                    />
                </div>

                <!-- Date -->
                <div v-else-if="cfg.control === 'date'" :class="['qf-group', widthClass(cfg)]">
                    <label class="qf-label">{{ cfg.label }}</label>
                    <input
                        type="date"
                        class="qf-input"
                        :value="String(localValues[configKey(cfg)] ?? '')"
                        @change="onDateChange(cfg, $event)"
                    />
                </div>

                <!-- Boolean Toggle -->
                <div v-else-if="cfg.control === 'boolean'" :class="['qf-group', widthClass(cfg)]">
                    <label class="qf-label">{{ cfg.label }}</label>
                    <div class="qf-toggle-group">
                        <button
                            :class="['qf-toggle', { 'qf-toggle--active': localValues[configKey(cfg)] === true }]"
                            @click="onBooleanToggle(cfg, true)"
                        >Yes</button>
                        <button
                            :class="['qf-toggle', { 'qf-toggle--active': localValues[configKey(cfg)] === false }]"
                            @click="onBooleanToggle(cfg, false)"
                        >No</button>
                    </div>
                </div>

                <!-- Select (in row 2) -->
                <div v-else-if="cfg.control === 'select'" :class="['qf-group', widthClass(cfg)]">
                    <label class="qf-label">{{ cfg.label }}</label>
                    <FilterSelect
                        :model-value="String(localValues[configKey(cfg)] ?? '')"
                        :options="fieldOptions[cfg.field] ?? cfg.options ?? []"
                        :placeholder="cfg.placeholder || 'All'"
                        @update:model-value="(val: string) => onSelectChange(cfg, val)"
                    />
                </div>
            </template>

            <!-- Clear all + Advanced link -->
            <div class="qf-group qf-group--actions">
                <label class="qf-label">&nbsp;</label>
                <div class="qf-actions">
                    <button
                        v-if="activeCount > 0"
                        class="qf-clear-link"
                        @click="onClearAll"
                    >
                        Clear all ({{ activeCount }})
                    </button>
                </div>
            </div>
        </div>

        <!-- Footer with advanced link -->
        <div class="qf-footer">
            <button class="qf-advanced-link" @click="emit('open-advanced')">
                Advanced filters →
            </button>
        </div>
    </div>
</template>

<style scoped>
.quick-filter-panel {
    background: var(--bg-surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    padding: 0.75rem;
    margin-top: 0.5rem;
}

.qf-row {
    display: flex;
    gap: 0.625rem;
    align-items: flex-end;
    flex-wrap: wrap;
}

.qf-row--secondary {
    margin-top: 0.625rem;
    padding-top: 0.625rem;
    border-top: 1px solid var(--border-color);
}

.qf-group {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 120px;
}

.qf-group--narrow {
    min-width: 90px;
    max-width: 110px;
}

.qf-group--wide {
    flex: 1;
    min-width: 180px;
}

.qf-group--actions {
    margin-left: auto;
    min-width: auto;
}

.qf-label {
    font-size: var(--text-xs);
    font-weight: 500;
    color: var(--text-muted);
}

.qf-input {
    padding: 0.375rem 0.5rem;
    background: var(--input-bg);
    border: 1px solid var(--input-border);
    border-radius: var(--radius-sm);
    color: var(--text-primary);
    font-size: var(--text-sm);
    font-family: inherit;
    transition: border-color 0.15s;
}

.qf-input:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgba(18, 192, 221, 0.1);
}

.qf-input--number {
    font-variant-numeric: tabular-nums;
    -moz-appearance: textfield;
}

.qf-input--number::-webkit-inner-spin-button,
.qf-input--number::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.qf-input::placeholder {
    color: var(--text-disabled);
}

/* Search */
.qf-search-wrapper {
    position: relative;
}

.qf-search-icon {
    position: absolute;
    left: 0.5rem;
    top: 50%;
    transform: translateY(-50%);
    width: 14px;
    height: 14px;
    color: var(--text-muted);
    pointer-events: none;
}

.qf-search-input {
    width: 100%;
    padding: 0.375rem 0.625rem 0.375rem 1.75rem;
    background: var(--input-bg);
    border: 1px solid var(--input-border);
    border-radius: var(--radius-sm);
    color: var(--text-primary);
    font-size: var(--text-sm);
    font-family: inherit;
    transition: border-color 0.15s, box-shadow 0.15s;
}

.qf-search-input::placeholder { color: var(--text-disabled); }
.qf-search-input:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgba(18, 192, 221, 0.1);
}

/* Boolean Toggle */
.qf-toggle-group {
    display: flex;
    border: 1px solid var(--input-border);
    border-radius: var(--radius-sm);
    overflow: hidden;
}

.qf-toggle {
    padding: 0.375rem 0.625rem;
    font-size: var(--text-xs);
    font-weight: 500;
    color: var(--text-muted);
    background: var(--input-bg);
    border: none;
    cursor: pointer;
    transition: all 0.15s;
    flex: 1;
    text-align: center;
}

.qf-toggle + .qf-toggle {
    border-left: 1px solid var(--input-border);
}

.qf-toggle:hover {
    color: var(--text-primary);
    background: var(--bg-hover);
}

.qf-toggle--active {
    color: var(--color-primary);
    background: rgba(18, 192, 221, 0.1);
    font-weight: 600;
}

/* Actions */
.qf-actions {
    display: flex;
    align-items: center;
    height: 30px;
}

.qf-clear-link {
    padding: 0;
    border: none;
    background: none;
    font-size: var(--text-xs);
    color: var(--color-primary);
    cursor: pointer;
    white-space: nowrap;
}

.qf-clear-link:hover {
    text-decoration: underline;
}

/* Footer */
.qf-footer {
    display: flex;
    justify-content: center;
    padding-top: 0.5rem;
    margin-top: 0.5rem;
    border-top: 1px solid var(--border-color);
}

.qf-advanced-link {
    padding: 0;
    border: none;
    background: none;
    font-size: var(--text-xs);
    color: var(--text-muted);
    cursor: pointer;
    transition: color 0.15s;
}

.qf-advanced-link:hover {
    color: var(--color-primary);
}

/* Select styling */
select.qf-input {
    appearance: none;
    -webkit-appearance: none;
    cursor: pointer;
    padding-right: 1.75rem;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%237a8599' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.5rem center;
    background-size: 12px;
}

select.qf-input:hover {
    border-color: var(--border-color-hover, var(--border-color));
}

select.qf-input option {
    background: var(--bg-surface);
    color: var(--text-primary);
    padding: 0.375rem 0.5rem;
}
</style>
